module Steps.Step1.View where

import Common

import API.Account (AccountType(..))
import API.Export (ExportType)
import API.Pagination (pageItems)
import API.RecentForms (RecentForm)
import API.SelStudent (SelStudent, studentName_)
import API.Types (StudentID)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Array (catMaybes, concat, groupBy, sortWith)
import Data.Array as Arr
import Data.Array.NonEmpty (NonEmptyArray)
import Data.Array.NonEmpty as NE
import Data.Map as Map
import Data.Maybe (isJust)
import Data.Set as Set
import Form.StudentSelect (renderTags, selectStudentModal)
import GraphQL.Type (innerId)
import Internal.Luxon as Luxon
import Internal.SelectionMap (SelectionMap, listSelected, selectAllCheckbox, selectionCheckbox')
import Internal.SelectionMap as SM
import Internal.Util (justIf)
import Steps.FormPicker (renderFormPicker)
import Steps.Step1.State (RecentFormState, RecentFormStateFlag(..), selectedForms)
import Steps.Step1.Update (DocExport, Msg(..), RecentFormAction(..), UpdateFormsSelection(..), UpdateFormsType(..))
import Widget.Pager (pagerWidget)
import Widgets.BetterSelect (setSelected)
import Widgets.Checkbox (checkbox)
import Widgets.Confirmation (confirmModal)
import Widgets.Dropdown (dropdownButton, dropdownButton_)
import Widgets.Loading (loadingWidget)
import Widgets.Modal (ModalResult(..))
import Widgets.Search (searchWidget)
import Widgets.Signature.Request (signatureRequestModal)
import Widgets.Signature.Types (FormSigField, SigRelation)
import Widgets.UpdateForm (semesterUpdateModal, teacherUpdateModal)

step1MessageWidget :: RecentFormState -> Widget HTML Msg
step1MessageWidget state = do
    res <- renderWidget state
    case res of
        Left state' ->
            pure $ StateChanged state'
        Right act | modalActionWhileLoading act ->
            -- Modal widgets run before App.Update can apply operation guards.
            pure $ ViewAction act
        Right CopyDocuments ->
            CopyStudentsSelected <$> copyStudentsModal state
        Right DeleteDocuments ->
            DeleteConfirmed <$> confirmModal (renderRecentForms state)
                (lookupTranslation "confirm-delete-forms-title")
                (lookupTranslation "confirm-delete-forms-primary-button")
                (lookupTranslation "confirm-delete-forms")
        Right (RequestSignatures rel) ->
            SignatureRequestsSelected <$> signatureRequestModal state.formPicker.workQueueCache.account (selectedForms state) rel
        Right (UpdateForms uftype) ->
            FormsUpdateSelected <$> updateFormsSelectionModal state uftype
        Right act ->
            pure $ ViewAction act
  where
    modalActionWhileLoading action = case state.state of
        RFLoading -> case action of
            CopyDocuments -> true
            DeleteDocuments -> true
            RequestSignatures _ -> true
            UpdateForms _ -> true
            _ -> false
        _ ->
            false

copyStudentsModal :: RecentFormState -> Widget HTML (Maybe (SelectionMap StudentID SelStudent))
copyStudentsModal state = do
    result <- selectStudentModal (D.div' []) (lookupTranslation "copy-forms-select-students") $
        Just (state.searchState { teacher = setSelected state.searchState.teacher Nothing })
    pure case result of
        ModalClosed -> Nothing
        ModalReturned sstate -> Just sstate.students

updateFormsSelectionModal :: RecentFormState -> UpdateFormsType -> Widget HTML (Maybe UpdateFormsSelection)
updateFormsSelectionModal state = case _ of
    UpdateSemester ->
        map UpdateSemesterSelected <$> semesterUpdateModal (selectedForms state)
    UpdateTeacher ->
        map UpdateTeacherSelected <$> teacherUpdateModal (selectedForms state)

renderWidget :: RecentFormState -> Widget HTML (Either RecentFormState RecentFormAction)
renderWidget state = D.main
    [ P.className "step-container step-1", P.role "main" ]
    [ D.header
        [ P.className "page-header" ] $ catMaybes
        [ Just $ D.h1
            [ P.className "page-heading" ]
            [ D.text $ lookupTranslation "recent-forms" ]
        , justIf bulkActionsVisible $
            -- Mystery Meat Land
            -- What could this navigation be? Have a click and see!
            -- Mystery meat, for you and for me!
            -- This one looks like a treat, should I click?
            -- It signed all my forms, oh what a mean trick!
            D.div
                [ P.className "mm-land" ] $ catMaybes
                [ Just $ D.button
                    [ P.className "edit-forms-button btn"
                    , P.title $ lookupTranslation "edit-forms"
                    , P.disabled busy
                    , Right EditDocuments <$ P.onClick
                    ]
                    [ D.span [ P.className "edit-forms icon" ] [] ]
                , justIf canToggleLocks $ D.button
                    [ P.className $ "toggle-lock-forms-button btn " <> (toggleLocksClass <> "-button")
                    , P.title $ toggleLocksLabel
                    , P.disabled busy
                    , Right ToggleDocumentLocks <$ P.onClick
                    ]
                    [ D.span [ P.className $ "icon " <> (toggleLocksClass <> "-forms") ] [] ]
                , Just $ D.button
                    [ P.className "copy-forms-button btn"
                    , P.title $ lookupTranslation "copy-forms"
                    , P.disabled busy
                    , Right CopyDocuments <$ P.onClick
                    ]
                    [ D.span [ P.className "copy-forms icon" ] [] ]
                , justIf (state.formPicker.workQueueCache.account.type /= StudentAccount) $ D.button
                    [ P.className "delete-forms-button btn"
                    , P.title $ lookupTranslation "delete-forms"
                    , P.disabled busy
                    , Right DeleteDocuments <$ P.onClick
                    ]
                    [ D.span [ P.className "delete-forms icon" ] [] ]
                , Just $ renderExportDropdown state
                , Just $ renderEmailDropdown state
                , Just $ renderMiscDropdown state
                ]
        , Just $ D.button
            [ P.className "create-forms-button btn btn-primary"
            , Right CreateForms <$ P.onClick
            ]
            [ D.text $ lookupTranslation "create-forms" ]
        ]
    , D.div [ P.className "form-picker-container" ]
        [ formPickerEdit
        , Right <$> FormPickerChanged <$> renderFormPicker state.formPicker
        ]
    , D.div
        [ P.className "step-1-right" ]
        [ renderRecentForms state ]
    ]
  where
    bulkActionsVisible = not $ SM.isEmpty state.selection

    busy = case state.state of
        RFLoading -> true
        _ -> false

    selected = Arr.fromFoldable $ listSelected state.selection
    canToggleLocks = not (Arr.null selected) && Arr.all (\form -> form.can.lock) selected
    formPickerEdit | state.formPicker.reorder =
        D.button
            [ Right SaveFormOrder <$ P.onClick
            , P.className $ "form-picker-re-order-save button btn"
            ]
            [ D.text $ lookupTranslation "save" ]
    formPickerEdit =
        D.button
            [ Right StartFormReorder <$ P.onClick
            , P.className $ "form-picker-re-order button btn"
            ]
            [ D.span [ P.className "re-order-icon" ] [], D.text $ lookupTranslation "re-order" ]

    toggleLocksLabel :: String
    toggleLocksLabel = if everyDocumentLocked then "Unlock" else "Lock"

    toggleLocksClass :: String
    toggleLocksClass = if everyDocumentLocked then "unlock" else "lock"

    everyDocumentLocked :: Boolean
    everyDocumentLocked = Arr.all (isJust <<< _.locked_at) $ Arr.fromFoldable $ listSelected state.selection

renderExportDropdown :: RecentFormState -> Widget HTML (Either RecentFormState RecentFormAction)
renderExportDropdown state = Right <$> ExportDocuments <$> dropdownButton btn items
  where
    allExports :: Array (Array ExportType)
    allExports = map _.exports $ Arr.fromFoldable $ listSelected state.selection

    groupedExports :: Array (NonEmptyArray ExportType)
    groupedExports = groupBy (\a b -> a.label == b.label) $
        sortWith _.label $ concat allExports

    combineExports :: NonEmptyArray ExportType -> DocExport
    combineExports exps = let id = (NE.head exps).label in
        { id, label: id }

    exports :: Array DocExport
    exports = map combineExports groupedExports

    items = map (\exp -> { label: exp.label, value: exp }) exports

    btn = D.button
        [ P.className "export-forms-button btn"
        , P.title $ lookupTranslation "export-forms"
        , P.disabled busy
        , P.onClick
        ]
        [ D.span [ P.className "export-forms icon" ] [] ]
    busy = case state.state of
        RFLoading -> true
        _ -> false

renderMiscDropdown :: RecentFormState -> Widget HTML (Either RecentFormState RecentFormAction)
renderMiscDropdown state = Right <$> UpdateForms <$> dropdownButton_ btn items
  where
    items =
        [ updateFormsItem UpdateSemester (lookupTranslation "update-semester")
        , updateFormsItem UpdateTeacher (lookupTranslation "update-teacher")
        ]

    updateFormsItem action label =
        if nonUpdatableFormSelected
            then D.a
                [ P.className "dropdown-item disabled whitespace-nowrap"
                , P.title $ lookupTranslation "update-forms-disabled-reason-lock"
                ]
                [ D.text label ]
            else D.a
                [ action <$ P.onClick
                , P.className "dropdown-item whitespace-nowrap"
                ]
                [ D.text label ]

    nonUpdatableFormSelected = Arr.any (not <<< _.can.update) $ selectedForms state

    btn = D.button
        [ P.className "btn vdots"
        , P.disabled busy
        , P.onClick
        ]
        [ D.text "⠇" ]
    busy = case state.state of
        RFLoading -> true
        _ -> false

renderEmailDropdown :: RecentFormState -> Widget HTML (Either RecentFormState RecentFormAction)
renderEmailDropdown state = if Arr.length items > 0
    then Right <$> RequestSignatures <$> dropdownButton btn items
    else dropdownButton_ btn
        [ D.div
            [ P.className "dropdown-item whitespace-nowrap" ]
            [ D.text $ lookupTranslation "request-signatures-no-sigs" ]
        ]
  where
    allRels :: Map SigRelation String
    allRels = Map.fromFoldable $
        Arr.concatMap (\doc -> docRels doc.form_type.signatures) $
        Arr.fromFoldable $ listSelected state.selection

    docRels :: Array FormSigField -> Array (Tuple SigRelation String)
    docRels sigs = map (\s -> Tuple s.relationship s.relationship_label) $
        Arr.filter (_.can.request) sigs

    items = map (\(Tuple rel label) -> { label, value: rel }) $ Map.toUnfoldable allRels

    btn = D.button
        [ P.className "request-sigs-button btn"
        , P.title $ lookupTranslation "request-signatures"
        , P.disabled busy
        , P.onClick
        ]
        [ D.span [ P.className "request-sigs icon" ] [] ]
    busy = case state.state of
        RFLoading -> true
        _ -> false

renderRecentForms :: RecentFormState -> Widget HTML (Either RecentFormState RecentFormAction)
renderRecentForms state = D.section
    [ P.className "recent-forms"
    , P.key "recent-forms-list"
    ]
    [ D.div
        [ P.className "recent-form-group" ]

        -- TODO: Finish recent-forms headings dropdown
        [ D.div
            [ P.className "dropdown" ]
            []
        ]

    , Right <$> SearchChanged <$> searchWidget state.searchState
    , case state.state of
        RFNormal -> D.ul [ P.className "recent-form-list" ] listRecentForms
        RFLoading -> loadingWidget
        RFError err -> D.text $ "Error: " <> show err
    , Right <$> ChangePage <$> pagerWidget state.recentForms
    ]
    where
        listRecentForms | length (pageItems state.recentForms) == 0 =
            [ D.li'
                [ D.div
                    [ P.className "recent-forms-empty"
                    , P.dangerouslySetInnerHTML $ { __html: lookupTranslation "recent-forms-empty" }
                    ] []
                ]
            ]
        listRecentForms = [D.li' [selectAllCheck]] <> map renderRecentForm (pageItems state.recentForms)

        selectAllCheck :: Widget HTML (Either RecentFormState RecentFormAction)
        selectAllCheck = Left <$> state { selection = _ } <$>
            selectAllCheckbox formTuples render state.selection
          where
            selCount = length $ SM.listSelected state.selection
            formTuples = map (\f -> Tuple f.id f) (pageItems state.recentForms)
            render selected = D.article
                [ P.className "student-form select-all" ]
                [ checkbox [ P._id "select-all-checkbox" ] selected
                , D.div [ P.className "info" ]
                    [ D.label
                        [ P.htmlFor "select-all-checkbox" ]
                        [ D.text $ if selCount > 0
                            then (show selCount <> lookupTranslation "selected-count")
                            else lookupTranslation "select-all" ]
                    ]
                ]

        renderRecentForm :: RecentForm -> Widget HTML (Either RecentFormState RecentFormAction)
        renderRecentForm form = D.li' [ D.article
            [ P.className "student-form", P.key $ "recent-form-" <> innerId form.id ]
            [ D.label
                [ P.htmlFor $ innerId form.id <> "-checkbox" ]
                [ Left <$> studentFormCheck form
                , studentFormType form
                ]
            , D.div
                [ (Right $ EditDocument form) <$ P.onClick
                , P.className "recent-form-info-wrapper"
                ]
                [ studentFormInfo form
                , D.div
                    [ P.className "recent-form-extra" ]
                    [ studentFormTime form
                    , Right <$> renderFormIndicators form
                    ]
                ]
            ]
        ]

        renderFormIndicators :: RecentForm -> Widget HTML RecentFormAction
        renderFormIndicators form = D.div [ P.className "recent-form-indicators" ] $ catMaybes
            [ justIf (isJust form.locked_at) $ D.div [ P.className "recent-form--indicator recent-form-locked tooltip" ]
                [ D.div [ P.className "image-replace" ] []
                , D.div [ P.className "tip-content" ]
                    [ D.text $ lookupTranslation "locked" ]
                ]
            , justIf (commentCount > 0) $ D.div [ P.className "recent-form--indicator recent-form-comments tooltip" ]
                [ D.div [ P.className "image-replace" ] []
                , D.div [ P.className "tip-content" ]
                    [ D.text $ show commentCount <> " " <> lookupTranslation "comments" ]
                ]
            , justIf (attachCount > 0) $ D.div [ P.className "recent-form--indicator recent-form-attachments tooltip" ]
                [ D.div [ P.className "image-replace" ] []
                , D.div [ P.className "tip-content" ]
                    [ D.text $ show attachCount <> " " <> lookupTranslation "attachments" ]
                ]
            , justIf (fieldCount > 0) $ D.div [ P.className "recent-form--indicator recent-form-signatures tooltip" ]
                [ D.div [ P.className "image-replace" ] []
                , D.meter
                    [ P._id $ innerId form.id <> "-signature-meter"
                    , P.key $ innerId form.id <> "-signature-meter"
                    , P.min $ show 0, P.max $ show fieldCount, P.value $ show signedCount
                    ] [ D.text sigCountStr ]
                , D.div [ P.className "tip-content" ]
                    [ D.text $ sigCountStr <> " " <> lookupTranslation "signatures" ]
                ]
            ]
          where
            commentCount = Arr.length form.comments
            attachCount = Arr.length form.attachments
            sigCountStr = show signedCount <> " / " <> show fieldCount

            fieldKeys = Set.fromFoldable $ map _.key $ form.form_type.signatures
            sigKeys = Set.fromFoldable $ map _.key $ form.signatures
            fieldCount = Set.size fieldKeys
            signedCount = Set.size $ Set.intersection sigKeys fieldKeys

        studentFormCheck :: RecentForm -> Widget HTML RecentFormState
        studentFormCheck form = state { selection = _ } <$>
            selectionCheckbox' form.id form props state.selection
          where
            props = [ P._id $ innerId form.id <> "-checkbox" ]

        studentFormType :: forall a. RecentForm -> Widget HTML a
        studentFormType form = D.abbr
            [ P.title form.form_type.label
            , P.className "form-abbr"
            , P.style { backgroundColor: form.form_type.bgColor }
            ]
            [ D.text form.form_type.abbr ]

        studentFormInfo :: forall a. RecentForm -> Widget HTML a
        studentFormInfo form = D.div
            [ P.className "info" ]
            [ D.div
                [ P.className "recent-form-header" ]
                [ D.h2 [ P.className "recent-form--heading" ] $ studentName_ state.formPicker.workQueueCache.account form.student
                    (\name -> [ D.text name ])
                    (\{ legal, preferred } ->
                        [ D.text legal
                        , D.span
                            [ P.className "preferred-name-paren" ]
                            [ D.text (" (" <> preferred <> ")") ]
                        ])
                , renderTags form.student
                ]
            , D.text $ form.subtitle <> " " <>
                if isJust form.legacy_url then lookupTranslation "legacy-form-label" else ""
            ]

        studentFormTime :: forall a. RecentForm -> Widget HTML a
        studentFormTime form =
            case Luxon.fromISO form.updated_at of
                Nothing ->
                    D.time
                        [ P.className "time"
                        , P.dateTime form.updated_at
                        ]
                        [ D.text "Failed parsing datetime" ]

                Just updatedAt ->
                    D.time
                        [ P.className "time"
                        , P.dateTime form.updated_at
                        , P.title $ Luxon.toLocaleString Luxon.dateTimeFull updatedAt
                        ]
                        [ D.text $ Luxon.toRelative updatedAt ]
